// Mock data for british-sign.co.uk refreshed homepage.
// Specific numbers (learner counts, dictionary size) are intentionally
// avoided unless verifiable. BSL Act 2022 is the official UK recognition.

// Learner course login and the buyer/admin enrolments dashboard both live on
// the enrol. subdomain (course login moved here from www/course-login).
export const LOGIN_URL = "https://enrol.british-sign.co.uk/course-login/";
export const ENROLMENTS_URL = "https://enrol.british-sign.co.uk/enrolments/";
// Our active Facebook page - it gets the Sign of the Day daily, so we point
// people to it from the footer, the Sign of the Day page and Contact.
export const FACEBOOK_URL = "https://www.facebook.com/BSL.British.Sign.Language";
// Real organisations using the course, taken from the live site
// plus additional verified institutions provided by the team.
// Logos are downloaded locally so the static build is self-contained.
// Order: household-name brands first (used by the enrol page's top-12 slice),
// then the rest of the network.
export const trustedByLogos = [
  { src: "/images/logos/bbc.png", alt: "BBC" },
  { src: "/images/logos/nhs.png", alt: "NHS" },
  { src: "/images/logos/vodafone.svg", alt: "Vodafone" },
  { src: "/images/logos/nationwide.png", alt: "Nationwide Building Society" },
  { src: "/images/logos/network-rail.png", alt: "Network Rail" },
  { src: "/images/logos/wellcome-trust.png", alt: "Wellcome Trust" },
  { src: "/images/logos/gatwick-airport.png", alt: "Gatwick Airport" },
  { src: "/images/logos/great-western-railway.jpg", alt: "Great Western Railway" },
  { src: "/images/logos/cambridge.svg", alt: "University of Cambridge" },
  { src: "/images/logos/kcl.svg", alt: "King’s College London" },
  { src: "/images/logos/scouts.png", alt: "The Scouts" },
  { src: "/images/logos/frimley-health-nhs.png", alt: "Frimley Health NHS Foundation Trust" },
  { src: "/images/logos/york-minster.png", alt: "York Minster" },
  { src: "/images/logos/brightonsussex.png", alt: "Brighton & Sussex Medical School" },
  { src: "/images/logos/nottingham.svg", alt: "University of Nottingham" },
  { src: "/images/logos/cardiff.svg", alt: "Cardiff University" },
  { src: "/images/logos/mmu.png", alt: "Manchester Metropolitan University" },
  { src: "/images/logos/anglia-ruskin-student-unoin.png", alt: "Anglia Ruskin Students’ Union" },
  { src: "/images/logos/chester-students-union.png", alt: "University of Chester Students’ Union" },
  { src: "/images/logos/fire-brigades-union.png", alt: "Fire Brigades Union" },
  { src: "/images/logos/sense.gif", alt: "Sense" },
  { src: "/images/logos/affinity-trust.png", alt: "Affinity Trust" },
  { src: "/images/logos/hampshire-cc.png", alt: "Hampshire County Council" },
  { src: "/images/logos/staffordshire-council.png", alt: "Staffordshire County Council" },
  { src: "/images/logos/city-of-lincoln-council.png", alt: "City of Lincoln Council" },
  { src: "/images/logos/south-yneside-council.png", alt: "South Tyneside Council" },
  { src: "/images/logos/towerbridge-exhibition.png", alt: "Tower Bridge Exhibition" },
  { src: "/images/logos/renaissance-care.png", alt: "Renaissance Care" },
  { src: "/images/logos/southdown-housing-association.png", alt: "Southdown Housing Association" },
  { src: "/images/logos/lancaster-and-morecambe-college.jpg", alt: "Lancaster & Morecambe College" },
  { src: "/images/logos/the-outside-clinic.png", alt: "The Outside Clinic" },
  { src: "/images/logos/arun-vets.png", alt: "Arun Vets" },
  { src: "/images/logos/next-step-independence.png", alt: "Next Step Independence" },
  { src: "/images/logos/Lincoln-Minster-School.png", alt: "Lincoln Minster School" },
  { src: "/images/logos/Penistone-grammar.png", alt: "Penistone Grammar School" },
  { src: "/images/logos/carr-hill-high-school.png", alt: "Carr Hill High School" },
  { src: "/images/logos/pittville-school.jpg", alt: "Pittville School" },
  { src: "/images/logos/smithswood.png", alt: "Smith’s Wood Academy" },
  { src: "/images/logos/buglawton-primary-school.png", alt: "Buglawton Primary School" },
  { src: "/images/logos/futasia-kids-club.png", alt: "Futasia Kids Club" },
  { src: "/images/logos/papillon-nursery.jpg", alt: "Papillon Nursery" },
  { src: "/images/logos/poppies-daycare.jpg", alt: "Poppies Daycare" },
  { src: "/images/logos/happy-serious.png", alt: "Happy & Serious" },
];

// Top-level nav uses a nested structure: items without `children` render as
// a direct link; items with `children` render as a hover/tap dropdown.
export const navLinks = [
  {
    label: "Learn",
    mega: true,
    // The paid course is the primary goal, so it takes the prominent lead
    // slot instead of sitting mid-list. The two columns split "getting
    // started" from background reading so the flat grab-bag has a logic.
    lead: {
      label: "The online BSL course",
      href: "/enrol",
      desc: "Pay What You Can · two years’ access. Start any time.",
    },
    groups: [
      {
        title: "Start learning",
        items: [
          { label: "Start here", href: "/start-here", desc: "New to BSL? Pick the right next step." },
          { label: "Where can I learn BSL?", href: "/where-can-i-learn-bsl" },
          { label: "Lesson plans", href: "/lesson-plans" },
        ],
      },
      {
        title: "About BSL & Deaf culture",
        items: [
          { label: "What is BSL?", href: "/what-is-bsl" },
          { label: "Deaf community", href: "/deaf-community" },
          { label: "Left-handed signing", href: "/left-handed-signing" },
          { label: "BSL books", href: "/bsl-books" },
        ],
      },
    ],
  },
  { label: "Dictionary", href: "/bsl-dictionary" },
  {
    label: "Free resources",
    mega: true,
    lead: {
      label: "All free resources",
      href: "/free-resources",
      desc: "Every free BSL tool, game and printable in one place.",
    },
    // Three groups so every item has one obvious home:
    //  - Fingerspelling = the topic hub. Everything fingerspelling-specific
    //    lives here - learn (chart, player, quiz) AND the fingerspelling
    //    make/print + maze tools - because that's the label people hunt with,
    //    and they expect every fingerspelling tool under it.
    //  - Make & print = the non-fingerspelling printables: the maker tools
    //    (greetings card, vocab sheet) plus ready-made charts to print
    //    (family signs, greetings signs).
    //  - Games & daily = play (the general games + fingerspelling arcade games
    //    that also read as "play").
    groups: [
      {
        title: "Fingerspelling",
        items: [
          { label: "Fingerspelling alphabet chart", href: "/bsl-fingerspelling-alphabet-chart", illus: "hub", desc: "Print the full A–Z BSL alphabet chart." },
          { label: "Fingerspelling Player", href: "/fingerspell", illus: "player", desc: "Spell any word slowly on a big-screen player." },
          { label: "Word/Name Printer", href: "/print-a-word-in-fingerspelling", illus: "printer", desc: "Print any word or name in BSL fingerspelling." },
          { label: "Word Search Maker", href: "/fingerspelling-word-search-maker", illus: "wordsearch", desc: "Make a fingerspelling word search by topic." },
          { label: "Fingerspell Maze", href: "/fingerspell-maze", illus: "maze", desc: "Pac-Man-style maze: collect the letters in order." },
          { label: "Fingerspelling Quiz", href: "/fingerspell/quiz", illus: "fingerspellquiz", desc: "Read fingerspelled words against the clock." },
        ],
      },
      {
        title: "Games & daily",
        items: [
          { label: "Daily BSL Practice", href: "/daily", illus: "daily", desc: "Four short BSL activities every day, with a streak tracker.", isNew: true },
          { label: "BSL Sign Quiz", href: "/bsl-sign-quiz", illus: "signquiz", desc: "Guess the sign in four play modes.", isNew: true },
          { label: "Signspell", href: "/signspell", illus: "signspell", desc: "Daily BSL fingerspelling word puzzle." },
          { label: "Fingerspelling Invaders", href: "/fingerspelling-invaders", illus: "invaders", desc: "Arcade game: blast the falling BSL handshapes.", isNew: true },
          { label: "BSL Sign Memory", href: "/sign-memory", illus: "memory", desc: "Match pairs of BSL signs from memory." },
          { label: "Fingerspell Five", href: "/daily-bsl-word-guess", illus: "wordguess", desc: "Work out the hidden five-letter BSL word." },
        ],
      },
      {
        title: "Make & print",
        items: [
          { label: "Greetings Card Maker", href: "/greetings-card-creator", illus: "card", desc: "Make a BSL greetings card for any occasion.", isNew: true },
          { label: "Vocab Sheet Maker", href: "/bsl-vocabulary-sheet-creator", illus: "vocab", desc: "Build a printable vocab sheet for any topic." },
          { label: "Family signs", href: "/bsl-family-signs", illus: "family", desc: "A one-page chart of family signs to print." },
          { label: "Greetings signs", href: "/bsl-greetings-signs", illus: "greetings", desc: "Hello, please, thank you – everyday signs to print." },
        ],
      },
    ],
    feature: {
      eyebrow: "Daily",
      title: "Sign of the Day",
      blurb: "A new British Sign Language sign every day, drawn from our BSL dictionary.",
      href: "/sign-of-the-day",
      cta: "See today’s sign",
      preview: "sotd",
    },
  },
  {
    label: "Organisations",
    children: [
      { label: "For schools", href: "/for-schools" },
      { label: "For libraries", href: "/for-libraries" },
      { label: "For tutors", href: "/for-tutors" },
      { label: "Scouts, Guides & youth groups", href: "/for-scouts-guides" },
      { label: "Duke of Edinburgh’s Award", href: "/dofe" },
      { label: "Groups & teams", href: "/groups" },
      { label: "BSL widgets for your site", href: "/embed" },
    ],
  },
];

export const heroStats = [
  { value: "10", label: "Self-study sections" },
  { value: "20 hours", label: "Estimated time" },
  { value: "CPD", label: "Certified course" },
  { value: "2 years", label: "Full course access" },
];

// Rotating hero images, sourced from the existing british-sign.co.uk slider
// and bundled locally so the static build is self-contained.
// `position` controls object-position so each photo can be cropped to taste.
// Order is randomised at render time (see Hero.jsx).
export const heroImages = [
  {
    src: "/images/hero/hero-1.jpg",
    alt: "A young woman smiling as she signs in British Sign Language.",
    position: "center center",
  },
  {
    src: "/images/hero/hero-2.jpg",
    alt: "A young man smiling while fingerspelling in British Sign Language.",
    position: "center center",
  },
  {
    src: "/images/hero/hero-3.jpg",
    alt: "A young boy practising British Sign Language at home.",
    position: "center center",
    category: "child",
  },
  {
    src: "/images/hero/hero-4.jpg",
    alt: "Two friends having a conversation in British Sign Language on a sofa.",
    position: "center center",
  },
  {
    src: "/images/hero/hero-5.jpg",
    alt: "A young girl smiling and signing in a classroom.",
    position: "center center",
    category: "child",
  },
];

export const courseModules = [
  { num: "01", title: "Fingerspelling & Numbers 1–10" },
  { num: "02", title: "Numbers 11–100, Colours & Help" },
  { num: "03", title: "Greetings & Facial Expressions" },
  { num: "04", title: "Animals, Home & Clothing" },
  { num: "05", title: "Family & Descriptions" },
  { num: "06", title: "Hobbies, Likes & Dislikes" },
  { num: "07", title: "Time, Tenses, Days & Months" },
  { num: "08", title: "Food, Drink & Money" },
  { num: "09", title: "Emotions & Conversation" },
  { num: "10", title: "Final Assessment" },
];

export const sotd = {
  word: "Coffee",
  image: "/images/sotd/coffee.png",
  imageBg: "#0B2A6B",
  letter: "C",
  note:
    "Fingerspelled \u2018C\u2019 hand held at shoulder height twists towards the mouth twice. A small, useful sign that comes up more than you\u2019d expect.",
  tip: "Try it next time you\u2019re waiting for the kettle.",
  dateLabel: "Today\u2019s sign",
  dateLong: "Sun 24 May 2026",
};

// All testimonials below are verbatim from real published reviews on
// british-sign.co.uk. Names and dates are sourced from the public course
// page (https://www.british-sign.co.uk/learn-online-british-sign-language-course/)
// where reviewer attribution is shown publicly. The quote text matches the
// records in wp_posts (post_type = 'site-review') exactly, including the
// original punctuation.
// NOTE: Real customer reviews now live in /src/data/reviews.json (scraped from
// the live /customer-reviews/ page). Testimonials.jsx imports and randomly
// samples that file on each mount.

export const footerColumns = [
  {
    heading: "Learn",
    links: [
      { label: "Start here", href: "/start-here" },
      { label: "Online course", href: "/enrol" },
      { label: "Gift the course", href: "/gift-a-bsl-course" },
      { label: "What is BSL?", href: "/what-is-bsl" },
      { label: "Deaf community", href: "/deaf-community" },
      { label: "Where can I learn BSL?", href: "/where-can-i-learn-bsl" },
      { label: "Left-handed signing", href: "/left-handed-signing" },
      { label: "BSL books", href: "/bsl-books" },
      { label: "Lesson plans", href: "/lesson-plans" },
    ],
  },
  {
    heading: "Free resources",
    links: [
      { label: "All free resources", href: "/free-resources" },
      { label: "BSL dictionary", href: "/bsl-dictionary" },
      { label: "Daily BSL Practice", href: "/daily" },
      { label: "Fingerspelling Invaders", href: "/fingerspelling-invaders" },
      { label: "Make and print", href: "/free-resources#make-and-print" },
      { label: "BSL games", href: "/free-resources#play-and-practise" },
      { label: "Printable resources", href: "/free-resources#ready-made-printables" },
      { label: "BSL widgets", href: "/embed" },
    ],
  },
  {
    heading: "For organisations",
    links: [
      { label: "Groups & teams", href: "/groups" },
      { label: "Schools", href: "/for-schools" },
      { label: "Libraries", href: "/for-libraries" },
      { label: "BSL tutors", href: "/for-tutors" },
      { label: "Scouts, Guides & youth groups", href: "/for-scouts-guides" },
      { label: "Duke of Edinburgh’s Award", href: "/dofe" },
    ],
  },
  {
    heading: "Help & information",
    links: [
      { label: "Course login", href: "https://enrol.british-sign.co.uk/course-login/", external: true },
      { label: "Enrolments dashboard", href: "https://enrol.british-sign.co.uk/enrolments/", external: true },
      { label: "Help centre", href: "https://help.british-sign.co.uk/", external: true },
      { label: "Contact", href: "/contact" },
      { label: "Customer reviews", href: "/customer-reviews" },
      { label: "Accessibility", href: "/accessibility" },
      { label: "Carbon footprint", href: "/carbon-footprint" },
      { label: "Sister sites", href: "/sister-sites" },
      { label: "Privacy", href: "/privacy-policy" },
      { label: "Terms", href: "/terms-conditions" },
      { label: "Refunds", href: "/refund-policy" },
      { label: "Cookies", href: "/cookies-policy" },
    ],
  },
];
